import * as React from 'react'
import Box from '@mui/material/Box'
import { styled, keyframes } from '@mui/material/styles'

interface IProps {
  activeStep: number
  steps: string[]
}

const shimmer = keyframes`
  0% {
    transform: translateX(-120%);
    opacity: 0;
  }
  20% {
    opacity: 0.5;
  }
  100% {
    transform: translateX(320%);
    opacity: 0;
  }
`

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(45, 116, 156, 0.24);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(45, 116, 156, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(45, 116, 156, 0);
  }
`

const StepperShell = styled(Box)(() => ({
  width: '100%',
  boxSizing: 'border-box',
  maxWidth: '100%',
  padding: '24px 20px 10px',
  borderRadius: '28px',
  background:
    'linear-gradient(180deg, rgba(255,255,255,0.82) 0%, rgba(246,250,254,0.9) 100%)',
  border: '1px solid rgba(182, 194, 214, 0.55)',
  boxShadow: '0 20px 45px rgba(32, 60, 86, 0.08)',
  backdropFilter: 'blur(10px)',
}))

const Track = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  height: 8,
  borderRadius: 999,
  background: 'linear-gradient(90deg, rgba(212,224,238,0.7) 0%, rgba(232,238,246,0.95) 100%)',
  overflow: 'hidden',
}))

const Progress = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'progress',
})<{ progress: number }>(({ progress }) => ({
  position: 'absolute',
  inset: 0,
  width: `${progress}%`,
  borderRadius: 999,
  background: 'linear-gradient(90deg, #2D749C 0%, #4797C4 65%, #6AB6DA 100%)',
  boxShadow: '0 8px 22px rgba(45, 116, 156, 0.24)',
  transition: 'width 320ms ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      'linear-gradient(110deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.42) 45%, rgba(255,255,255,0) 70%)',
    animation: `${shimmer} 2.8s ease-in-out infinite`,
  },
}))

const StepsGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: 16,
  marginTop: -18,
  position: 'relative',
  zIndex: 1,
}))

const StepCard = styled(Box, {
  shouldForwardProp: (prop) => !['isActive', 'isCompleted'].includes(String(prop)),
})<{ isActive: boolean; isCompleted: boolean }>(({ isActive, isCompleted }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
  textAlign: 'center',
  transition: 'transform 180ms ease, opacity 180ms ease',
  transform: isActive ? 'translateY(-2px)' : 'translateY(0)',
  opacity: isCompleted || isActive ? 1 : 0.78,
}))

const StepDot = styled(Box, {
  shouldForwardProp: (prop) => !['isActive', 'isCompleted'].includes(String(prop)),
})<{ isActive: boolean; isCompleted: boolean }>(({ isActive, isCompleted }) => ({
  width: 38,
  height: 38,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.9rem',
  fontWeight: 800,
  color: isActive ? '#2D749C' : isCompleted ? '#FFFFFF' : '#8E9BA8',
  background: isActive
    ? 'linear-gradient(180deg, #FFFFFF 0%, #F5FAFE 100%)'
    : isCompleted
    ? 'linear-gradient(135deg, #2D749C 0%, #4B9AC6 100%)'
    : '#FFFFFF',
  border: `2px solid ${isActive ? '#2D749C' : isCompleted ? '#2D749C' : '#D4E0EE'}`,
  boxShadow: isActive
    ? '0 10px 24px rgba(45, 116, 156, 0.18)'
    : isCompleted
    ? '0 10px 22px rgba(45, 116, 156, 0.18)'
    : '0 8px 18px rgba(44, 65, 89, 0.08)',
  animation: isActive ? `${pulse} 2s ease-in-out infinite` : 'none',
}))

const StepLabel = styled(Box, {
  shouldForwardProp: (prop) => !['isActive', 'isCompleted'].includes(String(prop)),
})<{ isActive: boolean; isCompleted: boolean }>(({ isActive, isCompleted }) => ({
  fontSize: '0.9rem',
  lineHeight: 1.3,
  fontWeight: isActive || isCompleted ? 700 : 500,
  color: isActive || isCompleted ? '#23415B' : '#8A96A3',
  maxWidth: 160,
  transition: 'color 180ms ease',
}))

export function Steps(props: IProps) {
  const { activeStep, steps = [] } = props
  const safeStep = Math.min(Math.max(activeStep || 0, 0), Math.max(steps.length - 1, 0))
  const progress = steps.length > 1 ? (safeStep / (steps.length - 1)) * 100 : 0

  return (
    <StepperShell>
      <Track>
        <Progress progress={progress} />
      </Track>
      <StepsGrid>
        {steps.map((label, index) => {
          const isCompleted = index < safeStep
          const isActive = index === safeStep

          return (
            <StepCard key={`${label}-${index}`} isActive={isActive} isCompleted={isCompleted}>
              <StepDot isActive={isActive} isCompleted={isCompleted}>
                {isCompleted ? '✓' : index + 1}
              </StepDot>
              <StepLabel isActive={isActive} isCompleted={isCompleted}>
                {label}
              </StepLabel>
            </StepCard>
          )
        })}
      </StepsGrid>
    </StepperShell>
  )
}

export default Steps
